/**
 * Footwork rules.
 *
 * Where a duellist may stand, and how fast they get there. This lives in the
 * ruleset rather than in the server's simulation because both halves need the
 * identical arithmetic: the room integrates it authoritatively, and the client
 * runs the same function to predict its own step between server ticks. Two
 * implementations of the same walk would drift, and the drift would show up as
 * a camera that fights the player.
 *
 * Everything here is pure and expressed on the floor plane. Height is not a
 * thing a duellist controls, so it is not a thing this file has an opinion on.
 */
import { COMBAT, MOVEMENT, clamp } from '@spell/shared-types';

export interface Stance {
  x: number;
  z: number;
}

export const STANCE_HALF_DISTANCE = COMBAT.DUEL_DISTANCE / 2;

/** The spot a duellist is seated at, given which end of the arena they took. */
export function stanceHome(side: number): Stance {
  return { x: 0, z: side * STANCE_HALF_DISTANCE };
}

/**
 * Which way is "toward the opponent" for this side, on the floor plane.
 *
 * Kept as a signed z rather than a vector because the arena axis is the z axis
 * and pretending otherwise would only invite a stray x term.
 */
export function stanceForwardZ(side: number): number {
  return -Math.sign(side) || -1;
}

/**
 * Pull a floor position back into this side's stance box.
 *
 * Depth is measured along the duellist's *own* forward axis, so "leaned in" and
 * "gave ground" mean the same thing at both ends of the arena and neither end
 * gets the more generous half of the box by accident.
 */
export function clampToStanceBox(side: number, x: number, z: number): Stance {
  const home = stanceHome(side);
  const fz = stanceForwardZ(side);
  const depth = clamp((z - home.z) * fz, -MOVEMENT.BOX_BACK, MOVEMENT.BOX_FORWARD);
  return {
    x: clamp(x, -MOVEMENT.BOX_HALF_X, MOVEMENT.BOX_HALF_X),
    z: home.z + depth * fz,
  };
}

/** True when this position is already inside the side's stance box. */
export function insideStanceBox(side: number, x: number, z: number): boolean {
  const c = clampToStanceBox(side, x, z);
  return Math.abs(c.x - x) < 1e-6 && Math.abs(c.z - z) < 1e-6;
}

/**
 * Advance a stance by one tick of held input.
 *
 * `forward` and `strafe` are the duellist's intent in their own frame, which is
 * what keeps W meaning "toward the opponent" for both players. The pair is
 * clamped to unit length here rather than taken on trust — that is what stops a
 * client from asking for diagonal speed it has not earned, and it is why the
 * server accepts a direction from the wire and never a position.
 *
 * Speed is passed in rather than read from the constants because the caller is
 * the one that knows whether a Slow is on: the rule about *how far* a duellist
 * may go belongs here, the rule about what is currently happening to them does
 * not.
 */
export function stepStance(
  side: number,
  from: Stance,
  forward: number,
  strafe: number,
  speed: number,
  dt: number,
): Stance {
  const magnitude = Math.hypot(forward, strafe);
  if (magnitude < 1e-4 || speed <= 0 || dt <= 0) {
    return clampToStanceBox(side, from.x, from.z);
  }
  const scale = (magnitude > 1 ? 1 / magnitude : 1) * speed * dt;
  const fz = stanceForwardZ(side);
  // right = cross(forward, up) with forward = (0,0,fz) and up = (0,1,0),
  // which is (-fz, 0, 0) — the same basis `aimDirection` builds its yaw on.
  return clampToStanceBox(
    side,
    from.x + -fz * strafe * scale,
    from.z + fz * forward * scale,
  );
}

/** Walking speed for a duellist in this condition, metres per second. */
export function stanceSpeed(slowed: boolean): number {
  return MOVEMENT.SPEED * (slowed ? MOVEMENT.SLOW_SCALE : 1);
}
